import React, { useEffect, useState } from "react";
import CompanyCard from "../common/CompanyCard";
import { Link } from "react-router-dom";
import { useSelector, useDispatch, connect } from "react-redux";
import PartyView from "./PartyView";
import PartyEdit from "./PartyEdit";
import Warning from "../common/Warning";
import { alertPermissionsRequired } from "../../actions/messages";
import { getApprovalWorkflows, getApprovalWorkflowStages } from "../../actions/approvalWorkflow";
import cologo0 from "../../assets/logo0.jpg";
import profile0 from "../../assets/profile0.jpg"
import { FormDiv, getRoleName } from "../common/Helpers";
import PartiesWrapper from "./PartiesWrapper";
import IconTick from '../iconComponents/IconTick';

export function ContractParties(props) {

    const noVendor = {
        name: "Vendor Company",
        city: "",
        state: "",
        industy: "",
        company_icon_path: cologo0
    }
    const noPurchaser = {
        name: "Purchaser Company",
        city: "",
        state: "",
        industy: "",
        company_icon_path: cologo0
    }
    const noPerson = {
        first_name: "",
        last_name: "",
        email: "",
        user_icon_path: profile0
    }

    const contract = useSelector((state) => state.contract.contract);
    const user = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();

    const userCanEdit = (user.croles.Creator === true) && (user.email === contract.PCA.email || user.email == contract.VCA.email);
    const pcaIsEditing = props.mode === "editparties" && user.email === contract.PCA.email;
    const vcaIsEditing = props.mode === "editparties" && user.email === contract.VCA.email;
    const pcaIsDrafting = pcaIsEditing && contract.contract_status === "Draft";
    const vcaIsDrafting = vcaIsEditing && contract.contract_status === "Draft";
    const purchasingCo = contract.fk_company_purchaser.name !== null ? contract.fk_company_purchaser : noPurchaser;
    const vendingCo = contract.fk_company_vendor.name !== null ? contract.fk_company_vendor : noVendor;
    // Basic-tier companies operate as a single person, not an organization -- collapse the
    // company card, extra admin roles, and approval-workflow section down to just that person.
    const purchaserIsBasic = purchasingCo.is_basic === true;
    const vendorIsBasic = vendingCo.is_basic === true;
    // GitHub #625: an Enterprise-tier rep can reassign their own side's PCA/VCA at any time,
    // not just while still Drafting -- Basic tier is always a single person, so its rep field
    // stays permanently locked once the contract leaves Draft (vcaIsDrafting/pcaIsDrafting below
    // cover the Draft-time counterparty-invite case, which is unaffected by this)
    const pcaIsReassignable = pcaIsEditing && !purchaserIsBasic && contract.contract_status !== "Draft";
    const vcaIsReassignable = vcaIsEditing && !vendorIsBasic && contract.contract_status !== "Draft";
    // Collapsed everywhere, including while editing: a Basic-tier company is always exactly one
    // person, so AP/AR and the approval workflow are never a real choice -- the backend derives
    // and enforces them from the PCA/VCA email itself (see ContractViewSet.update's "up" branch),
    // so there is nothing here for a Basic-tier user to ever fill in or see.
    const hidePurchaserExtras = purchaserIsBasic;
    const hideVendorExtras = vendorIsBasic;
    const showWarning = props.mode === "editparties" && props.up === "True";
    // In Draft, sending the invitations IS the action, so it's allowed once the other side's rep has
    // been entered -- whether or not the form was touched (e.g. already pre-filled from a converted
    // Quote). Once sent, submitting is an edit, which only makes sense if something actually changed.
    const counterpartyRole = contract.company_creator_type === "Vendor" ? "PCA" : "VCA";
    const canSubmitParties = contract.contract_status === "Draft"
        ? !!props.newParties[counterpartyRole]?.trim()
        : props.up === "True";

    // Approval Workflow templates available to the PCA -- the 2 global ones plus (if Enterprise)
    // this company's own, already filtered server-side. Only needed while actively picking.
    const templates = useSelector((state) => state.approvalWorkflow.workflows);
    useEffect(() => {
        if (pcaIsEditing) {
            dispatch(getApprovalWorkflows());
        }
    }, [pcaIsEditing]);

    const handleSelectTemplate = (template) => {
        dispatch(getApprovalWorkflowStages(template.uuid));
        props.onSelectTemplate(template);
    };

    // default to the global "1 Approval Level" template once the list has loaded, if nothing is
    // already selected (a contract already using a different template keeps that selection)
    useEffect(() => {
        if (pcaIsEditing && !props.newParties.template_id && templates.length > 0) {
            const defaultTemplate = templates.find((template) => !template.fk_company && template.name === "1 Approval Level");
            if (defaultTemplate) handleSelectTemplate(defaultTemplate);
        }
    }, [templates, pcaIsEditing]);

    // the stages currently driving the form (see WorkflowSingle's activeStages) -- capped defensively
    // in case a company's max_approval_stages was ever raised beyond what a template actually has
    const approvalStages = props.approvalStages || [];

    const roleInfo = {
        PCA: "Represents the purchasing company on this contract, and can edit who else plays a role",
        VCA: "Represents the vendor company on this contract, and can edit who else plays a role",
        APP1: "Reviews and approves invoices related to this contract",
        APP2: "Provides the final review and approval for invoices related to this contract",
        AP: "Confirms when payment has been made on invoices related to this contract",
        AR: "Closes out invoices related to this contract by confirming that payment has been received",
    }

    const purchaserAdminBlock =
        // gap-above-s matches the top margin the company card box (which this box replaces when
        // Basic) would have had, so the top of this box lines up with the top of the other column
        <PartiesWrapper section="Purchaser Administrators" className={hidePurchaserExtras ? "gap-above-s" : undefined} >
            {vcaIsDrafting || pcaIsReassignable
            ? <PartyEdit role="PCA" title="Purchaser Rep" email={props.newParties.PCA} onChange={(e) => props.onChange(e)} roleInfo={roleInfo.PCA}/>
            : <PartyView role="PCA" title="Purchaser Rep" roleInfo={roleInfo.PCA} />
            }
            {!vcaIsDrafting && !hidePurchaserExtras &&
                (pcaIsEditing
                ? <PartyEdit role="AP" title="Accounts Payable" email={props.newParties.AP} onChange={(e) => props.onChange(e)} roleInfo={roleInfo.AP} />
                : <PartyView role="AP" title="Accounts Payable" roleInfo={roleInfo.AP} />
                )
            }
        </PartiesWrapper>

    const purchaserApprovalsBlock = !vcaIsDrafting && !hidePurchaserExtras &&
        <PartiesWrapper section="Purchaser Approvals" className="gap-above-m" >
            {pcaIsEditing
            ? <>
                <div className="input-basic">
                    <select
                        name="template_id"
                        id="template_id"
                        required
                        style={props.newParties.template_id ? { color: "#3C3B41" } : { color: "#919197" }}
                        onChange={(e) => {
                            const selected = templates.find((template) => template.uuid === e.target.value);
                            if (selected) handleSelectTemplate(selected);
                        }}
                        value={props.newParties.template_id || ""}
                    >
                        <option value="">Select an Approval Workflow</option>
                        {templates.map((template) => (
                            <option key={template.uuid} value={template.uuid}>{template.name}</option>
                        ))}
                    </select>
                    <label htmlFor="template_id">Approval Workflow<span> *</span></label>
                </div>
                <div className="gap-above-s" style={{ maxHeight: "320px", overflowY: "auto" }}>
                    {approvalStages.map((stage, index) => (
                        <PartyEdit
                            key={index}
                            role={"APP" + (index + 1)}
                            title={getRoleName(stage.stage_name || stage.name)}
                            email={props.newParties["APP" + (index + 1)]}
                            onChange={(e) => props.onChange(e)}
                            roleInfo={roleInfo.APP1}
                            required={stage.is_required}
                        />
                    ))}
                </div>
             </>
            :<>
                <div className='content1 light-black gap-below-s' ><IconTick className="iblock gap-right-xxs" height='12px' color='green' /> {approvalStages.length} Approval level{approvalStages.length === 1 ? "" : "s"}</div>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                    {approvalStages.map((stage, index) => (
                        <PartyView
                            key={index}
                            role={"APP" + (index + 1)}
                            title={getRoleName(stage.stage_name || stage.name)}
                            roleInfo={roleInfo.APP1}
                        />
                    ))}
                </div>
             </>
            }
        </PartiesWrapper>

    const vendorAdminBlock =
        // see purchaserAdminBlock above -- same reasoning for the top-alignment margin
        <PartiesWrapper section="Vendor Admininistrators" className={hideVendorExtras ? "gap-above-s" : undefined} >
            {pcaIsDrafting || vcaIsReassignable
            ? <PartyEdit role="VCA" title="Vendor Rep" email={props.newParties.VCA} onChange={(e) => props.onChange(e)} roleInfo={roleInfo.VCA} />
            : <PartyView role="VCA" title="Vendor Rep" roleInfo={roleInfo.VCA} />
            }
            {!pcaIsDrafting && !hideVendorExtras &&
                (vcaIsEditing
                ? <PartyEdit role="AR" title="Accounts Receivable" email={props.newParties.AR} onChange={(e) => props.onChange(e)} roleInfo={roleInfo.AR} />
                : <PartyView role="AR" title="Accounts Receivable" roleInfo={roleInfo.AR} />
                )
            }
        </PartiesWrapper>

    return (
        <>
            <FormDiv formCondition={props.mode === "editparties"} onSubmit={(e) => props.onSubmit(e)} >
                <div className="flex-row" style={{ flexWrap: "wrap", marginLeft: "-20px" }}>
                    <div className="gap-right-l party-section">
                        {/* When Basic (and not actively being edited/created -- that still needs the
                            full form until contract creation is reworked to auto-fill these), the
                            Administrators box takes the company card's own top slot instead of being
                            pushed down below an empty placeholder, so it lines up with whatever sits
                            in that same row on the other side. */}
                        {hidePurchaserExtras
                        ? purchaserAdminBlock
                        : <>
                            <div className="gap-above-s gap-below-m doc-section">
                                <h2 className="title2 heavy label">Purchaser Company</h2>
                                <CompanyCard company={purchasingCo} imageSource={purchasingCo.company_icon_path} />
                            </div>
                            {purchaserAdminBlock}
                            {purchaserApprovalsBlock}
                          </>
                        }
                    </div>

                    <div className="party-section" >
                        {hideVendorExtras
                        ? vendorAdminBlock
                        : <>
                            <div className="gap-above-s gap-below-m doc-section">
                                <h2 className="title2 heavy label">Vendor Company</h2>
                                <CompanyCard company={vendingCo} imageSource={vendingCo.company_icon_path} />
                            </div>
                            {vendorAdminBlock}
                          </>
                        }
                    </div>
                </div>
                {props.mode === "editparties" &&
                    <div className="gap-above-l">
                        <div className="flex-row flex-end" >
                                <button className="btn min-width sec gap-right-s" disabled={props.isSubmitting} onClick={(e) => props.onCancelPartyChanges(e)}>Cancel</button>
                                <button id="contract_invite_parties" className="btn min-width pri" type="submit" disabled={!canSubmitParties || props.isSubmitting} >{props.isSubmitting ? "Please wait..." : "Invite Parties"}</button>
                        </div>
                    </div>
                }
            </FormDiv>
        </>
    )
}

const mapState = (state) => ({
    // parties: state.contract.parties,
    // invitation: state.contract.invitation,
});

export default connect(mapState, { alertPermissionsRequired })(ContractParties);
